import { useEditor, EditorContent, Mark } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Italic, Underline, Strikethrough, List, ListOrdered, Heading2, Heading3, Link2, Quote, RemoveFormatting } from "lucide-react";

export const TEXT_COLORS = [
  ["oc-terracota", "#C4674F", "Terracota"], ["oc-musgo", "#3E5641", "Musgo"],
  ["oc-gris", "#6B7678", "Gris"], ["oc-rojo", "#B42318", "Rojo"], ["oc-azul", "#1F5FA8", "Azul"],
];

const ColorClass = Mark.create({
  name: "colorClass",
  addAttributes() { return { class: { default: null } }; },
  parseHTML() { return [{ tag: "span[class]", getAttrs: (el) => (/^oc-/.test(el.getAttribute("class") || "") ? {} : false) }]; },
  renderHTML({ HTMLAttributes }) { return ["span", HTMLAttributes, 0]; },
});

export function RichTextEditor({ value, onChange, testId }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, code: false, codeBlock: false, horizontalRule: false, link: { openOnClick: false, autolink: true } }),
      ColorClass,
    ],
    content: value || "",
    editorProps: { attributes: { class: "rich-text min-h-[140px] px-3 py-2 focus:outline-none", "data-testid": testId } },
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? "" : e.getHTML()),
  });
  if (!editor) return null;

  const btn = (active, onClick, Icon, title) => (
    <button type="button" title={title} aria-label={title} onClick={onClick}
      className={`rounded-sm p-1.5 ${active ? "bg-[#3E5641] text-white" : "text-[#3E5641] hover:bg-[#F1F3F3]"}`}>
      <Icon size={14} />
    </button>
  );
  const c = () => editor.chain().focus();
  function setLink() {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Enlace (https://…). Déjalo vacío para quitarlo.", prev);
    if (url === null) return;
    if (!url.trim()) c().unsetLink().run();
    else c().extendMarkRange("link").setLink({ href: url.trim(), target: "_blank" }).run();
  }

  return (
    <div className="rounded-sm border border-[#3E5641]/20 bg-white">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-[#3E5641]/10 p-1" data-testid={`${testId}-toolbar`}>
        {btn(editor.isActive("bold"), () => c().toggleBold().run(), Bold, "Negritas")}
        {btn(editor.isActive("italic"), () => c().toggleItalic().run(), Italic, "Cursivas")}
        {btn(editor.isActive("underline"), () => c().toggleUnderline().run(), Underline, "Subrayado")}
        {btn(editor.isActive("strike"), () => c().toggleStrike().run(), Strikethrough, "Tachado")}
        {btn(editor.isActive("heading", { level: 2 }), () => c().toggleHeading({ level: 2 }).run(), Heading2, "Título")}
        {btn(editor.isActive("heading", { level: 3 }), () => c().toggleHeading({ level: 3 }).run(), Heading3, "Subtítulo")}
        {btn(editor.isActive("bulletList"), () => c().toggleBulletList().run(), List, "Lista")}
        {btn(editor.isActive("orderedList"), () => c().toggleOrderedList().run(), ListOrdered, "Lista numerada")}
        {btn(editor.isActive("blockquote"), () => c().toggleBlockquote().run(), Quote, "Cita")}
        {btn(editor.isActive("link"), setLink, Link2, "Enlace")}
        <span className="mx-1 h-5 w-px bg-[#3E5641]/15" />
        {TEXT_COLORS.map(([cls, hex, name]) => (
          <button key={cls} type="button" title={`Color ${name}`} aria-label={`Color ${name}`}
            onClick={() => c().setMark("colorClass", { class: cls }).run()}
            className={`h-5 w-5 rounded-full border ${editor.isActive("colorClass", { class: cls }) ? "ring-2 ring-offset-1 ring-[#3E5641]" : "border-white"}`}
            style={{ backgroundColor: hex }} data-testid={`${testId}-color-${cls}`} />
        ))}
        {btn(false, () => c().unsetAllMarks().clearNodes().run(), RemoveFormatting, "Quitar formato")}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
